/**
 * Gráfico da estrutura a termo — aba Juros & Inflação (#220).
 *
 * Séries (chips multi-select, estilo pills da Curva de Mercado): Pré |
 * Implícita | Cupom IPCA — default Pré + Implícita. Cada série é dividida em
 * 2 dataKeys: trecho DENTRO da curva (sólido) e cauda EXTRAPOLADA (tracejada,
 * flag `extrapolado` do payload #218) — conectadas no ponto de fronteira.
 * Badge "extrapolado (flat-forward)" na legenda quando a cauda existe (hoje
 * em prod são 0 pontos; o estado funciona nos dois casos).
 *
 * Comparação as-of: as curvas comparadas entram tracejadas finas — Pré via
 * /api/curva-pre?data_ref= e, desde o #228 (item 2), Implícita/Cupom via
 * /api/ipca?data_ref= (`comparacaoIpca`) — as 3 séries comparáveis.
 *
 * Merge defensivo por VENCIMENTO (não por índice): grades de tamanhos
 * diferentes (principal × comparação × projetado) não desalinham pontos.
 */
import { useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertTriangle } from "lucide-react";
import type { CurvaPreResponse, IpcaResponse } from "../../../api/types";

export type SerieJuros = "pre" | "impl" | "cupom";

export const SERIES_JUROS: Array<{ key: SerieJuros; label: string; cor: string }> = [
  { key: "pre", label: "Pré", cor: "#00DAC8" },
  { key: "impl", label: "Implícita", cor: "#f59e0b" },
  { key: "cupom", label: "Cupom IPCA", cor: "#818cf8" },
];

const COR_COMPARACAO_PRE = "#7dd3fc"; // sky-300 — distinto do turquesa principal

interface Props {
  principal: CurvaPreResponse;
  ipca: IpcaResponse | null;
  comparacao: CurvaPreResponse | null;
  /** #228: implícita/cupom as-of da MESMA data da comparação (ou null). */
  comparacaoIpca: IpcaResponse | null;
  selecionadas: SerieJuros[];
  onToggle: (s: SerieJuros) => void;
}

export interface PontoJuros {
  venc: string; // YYYY-MM-DD (chave do merge)
  label: string; // "jun/27"
  du?: number;
  pre?: number;
  preX?: number; // cauda extrapolada (+ ponto de fronteira)
  impl?: number;
  implX?: number;
  cupom?: number;
  cupomX?: number;
  preCmp?: number;
  implCmp?: number;
  cupomCmp?: number;
  preExtrapolado?: boolean;
  implExtrapolado?: boolean;
  cupomExtrapolado?: boolean;
}

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function labelVenc(iso: string): string {
  // String-only (YYYY-MM-DD) — nunca new Date(iso): UTC parse deslocaria o
  // mês em timezones negativos (classe timezone do adversarial).
  const m = parseInt(iso.slice(5, 7), 10) - 1;
  return `${MESES[m] ?? iso.slice(5, 7)}/${iso.slice(2, 4)}`;
}

/** Preenche par (sólido, extrapolado) de uma série num mapa de pontos.
 * O 1º ponto extrapolado também recebe o último valor sólido (fronteira)
 * pra linha tracejada CONECTAR na sólida. */
function preencherSerie(
  pontos: Map<string, PontoJuros>,
  rows: Array<{ vencimento: string; du: number; valor: number; extrapolado: boolean }>,
  keySolida: keyof PontoJuros,
  keyExtrap: keyof PontoJuros,
  keyFlag: keyof PontoJuros,
) {
  const ordenadas = [...rows].sort((a, b) => a.vencimento.localeCompare(b.vencimento));
  let fronteira: { vencimento: string; valor: number } | null = null;
  for (const r of ordenadas) {
    const p = pontos.get(r.vencimento) ?? {
      venc: r.vencimento,
      label: labelVenc(r.vencimento),
    };
    p.du = p.du ?? r.du;
    if (r.extrapolado) {
      (p as unknown as Record<string, unknown>)[keyExtrap] = r.valor;
      (p as unknown as Record<string, unknown>)[keyFlag] = true;
    } else {
      (p as unknown as Record<string, unknown>)[keySolida] = r.valor;
      fronteira = { vencimento: r.vencimento, valor: r.valor };
    }
    pontos.set(r.vencimento, p);
  }
  // Conecta o tracejado na fronteira do sólido.
  const temExtrap = ordenadas.some((r) => r.extrapolado);
  if (temExtrap && fronteira) {
    const p = pontos.get(fronteira.vencimento);
    if (p) (p as unknown as Record<string, unknown>)[keyExtrap] = fronteira.valor;
  }
}

export function montarPontos(
  principal: CurvaPreResponse,
  ipca: IpcaResponse | null,
  comparacao: CurvaPreResponse | null,
  comparacaoIpca: IpcaResponse | null = null,
): PontoJuros[] {
  const pontos = new Map<string, PontoJuros>();
  preencherSerie(
    pontos,
    principal.grade.map((g) => ({
      vencimento: g.vencimento, du: g.du, valor: g.taxa_aa, extrapolado: g.extrapolado,
    })),
    "pre", "preX", "preExtrapolado",
  );
  if (ipca) {
    preencherSerie(
      pontos,
      ipca.projetado.map((g) => ({
        vencimento: g.vencimento, du: g.du, valor: g.implicita_aa, extrapolado: g.extrapolado,
      })),
      "impl", "implX", "implExtrapolado",
    );
    preencherSerie(
      pontos,
      ipca.projetado.map((g) => ({
        vencimento: g.vencimento, du: g.du, valor: g.cupom_ipca_aa, extrapolado: g.extrapolado,
      })),
      "cupom", "cupomX", "cupomExtrapolado",
    );
  }
  if (comparacao) {
    for (const g of comparacao.grade) {
      const p = pontos.get(g.vencimento) ?? {
        venc: g.vencimento,
        label: labelVenc(g.vencimento),
      };
      p.preCmp = g.taxa_aa;
      pontos.set(g.vencimento, p);
    }
  }
  if (comparacaoIpca) {
    for (const g of comparacaoIpca.projetado) {
      const p = pontos.get(g.vencimento) ?? {
        venc: g.vencimento,
        label: labelVenc(g.vencimento),
      };
      p.implCmp = g.implicita_aa;
      p.cupomCmp = g.cupom_ipca_aa;
      pontos.set(g.vencimento, p);
    }
  }
  return [...pontos.values()].sort((a, b) => a.venc.localeCompare(b.venc));
}

function fmtTaxa(n: number): string {
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface TooltipEntry {
  payload: PontoJuros;
}

function ChartTooltip({
  active, payload, selecionadas, comparacaoRef,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  selecionadas: SerieJuros[];
  comparacaoRef: string | null;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload;
  const linhas: Array<{ rotulo: string; valor: number | undefined; cor: string; extrap?: boolean }> = [];
  for (const s of SERIES_JUROS) {
    if (!selecionadas.includes(s.key)) continue;
    const solido = p[s.key] as number | undefined;
    const extrap = p[`${s.key}X` as keyof PontoJuros] as number | undefined;
    const flag = Boolean(p[`${s.key}Extrapolado` as keyof PontoJuros]);
    linhas.push({
      rotulo: s.label,
      valor: solido ?? extrap,
      cor: s.cor,
      extrap: solido === undefined && flag,
    });
  }
  return (
    <div
      className="bg-indra-azul-deep border border-white/10 rounded-md p-3 text-xs text-white/90"
      data-testid="juros-chart-tooltip"
    >
      <div className="font-semibold text-white mb-1">
        {p.label}
        {p.du !== undefined && <span className="text-white/50 font-normal"> · {p.du} du</span>}
      </div>
      <div className="font-mono space-y-0.5">
        {linhas.map((l) => (
          <div key={l.rotulo} style={{ color: l.cor }}>
            {l.rotulo}: {l.valor !== undefined ? `${fmtTaxa(l.valor)}% a.a.` : "—"}
            {l.extrap && <span className="text-amber-300"> (extrapolado)</span>}
          </div>
        ))}
        {p.preCmp !== undefined && selecionadas.includes("pre") && (
          <div style={{ color: COR_COMPARACAO_PRE }}>
            Pré {comparacaoRef ?? ""}: {fmtTaxa(p.preCmp)}% a.a.
          </div>
        )}
        {p.implCmp !== undefined && selecionadas.includes("impl") && (
          <div style={{ color: "#fbbf24", opacity: 0.75 }}>
            Implícita {comparacaoRef ?? ""}: {fmtTaxa(p.implCmp)}% a.a.
          </div>
        )}
        {p.cupomCmp !== undefined && selecionadas.includes("cupom") && (
          <div style={{ color: "#a5b4fc", opacity: 0.75 }}>
            Cupom {comparacaoRef ?? ""}: {fmtTaxa(p.cupomCmp)}% a.a.
          </div>
        )}
      </div>
    </div>
  );
}

export function CurvaJurosChart({
  principal, ipca, comparacao, comparacaoIpca, selecionadas, onToggle,
}: Props) {
  const pontos = useMemo(
    () => montarPontos(principal, ipca, comparacao, comparacaoIpca),
    [principal, ipca, comparacao, comparacaoIpca],
  );

  const temExtrapVisivel = useMemo(
    () =>
      pontos.some((p) =>
        selecionadas.some((s) => Boolean(p[`${s}Extrapolado` as keyof PontoJuros])),
      ),
    [pontos, selecionadas],
  );

  return (
    <div
      className="rounded-xl border border-white/10 bg-indra-azul-surface p-4 mb-4"
      data-testid="curva-juros-chart"
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-white/50">Séries</span>
          <div className="inline-flex flex-wrap gap-1" data-testid="pill-series">
            {SERIES_JUROS.map((s) => {
              const active = selecionadas.includes(s.key);
              const ultima = active && selecionadas.length === 1;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => !ultima && onToggle(s.key)}
                  data-testid={`pill-serie-${s.key}`}
                  data-active={active}
                  title={ultima ? "Ao menos uma série precisa ficar visível" : undefined}
                  className={
                    "px-3 py-1 rounded-full text-xs font-medium transition-colors border " +
                    (active
                      ? "bg-indra-turquesa/20 border-indra-turquesa text-indra-turquesa"
                      : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white/80")
                  }
                  style={active ? { borderColor: s.cor, color: s.cor, backgroundColor: `${s.cor}22` } : undefined}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
        {temExtrapVisivel && (
          <span
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] bg-amber-500/15 text-amber-300 border border-amber-400/30"
            data-testid="juros-badge-extrapolado"
            title="Vencimentos além do último vértice publicado pela ANBIMA — taxa mantém a forward do último intervalo da curva (flat-forward). Trecho renderizado tracejado."
          >
            <AlertTriangle size={10} />
            extrapolado (flat-forward)
          </span>
        )}
        {comparacao && (
          <span className="text-[11px]" style={{ color: COR_COMPARACAO_PRE }} data-testid="juros-cmp-legenda">
            ─ ─ as-of {comparacao.data_ref}
          </span>
        )}
      </div>

      <div className="h-[320px]" data-testid="juros-chart-canvas">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={pontos} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              stroke="rgba(255,255,255,0.5)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
              // Grade tem ~127 pontos mensais — minTickGap evita sopa de labels.
              minTickGap={32}
              interval="preserveStartEnd"
            />
            <YAxis
              stroke="rgba(255,255,255,0.5)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
              tickFormatter={(v: number) => `${v.toLocaleString("pt-BR")}%`}
              domain={["auto", "auto"]}
            />
            <Tooltip
              content={
                <ChartTooltip
                  selecionadas={selecionadas}
                  comparacaoRef={comparacao?.data_ref ?? null}
                />
              }
            />
            {SERIES_JUROS.filter((s) => selecionadas.includes(s.key)).map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stroke={s.cor}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
                name={s.label}
              />
            ))}
            {SERIES_JUROS.filter((s) => selecionadas.includes(s.key)).map((s) => (
              <Line
                key={`${s.key}X`}
                type="monotone"
                dataKey={`${s.key}X`}
                stroke={s.cor}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
                legendType="none"
                name={`${s.label} (extrapolado)`}
              />
            ))}
            {comparacao && selecionadas.includes("pre") && (
              <Line
                type="monotone"
                dataKey="preCmp"
                stroke={COR_COMPARACAO_PRE}
                strokeWidth={1.5}
                strokeDasharray="3 3"
                strokeOpacity={0.8}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
                name={`Pré ${comparacao.data_ref}`}
              />
            )}
            {comparacaoIpca && selecionadas.includes("impl") && (
              <Line
                type="monotone"
                dataKey="implCmp"
                stroke="#fbbf24"
                strokeWidth={1.5}
                strokeDasharray="3 3"
                strokeOpacity={0.6}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
                name={`Implícita ${comparacaoIpca.data_ref_implicita ?? ""}`}
              />
            )}
            {comparacaoIpca && selecionadas.includes("cupom") && (
              <Line
                type="monotone"
                dataKey="cupomCmp"
                stroke="#a5b4fc"
                strokeWidth={1.5}
                strokeDasharray="3 3"
                strokeOpacity={0.6}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
                name={`Cupom ${comparacaoIpca.data_ref_implicita ?? ""}`}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
